import {BaseStore, globalStore, StoreClass, StoreObject} from "../../../stem-core/state/Store";
import {Money} from "../../../stem-core/localization/Money.js";
import {apiClient} from "../../connection/BlinkApiClient.js";
import {field, FieldDescriptor} from "../../../stem-core/state/StoreField.js";
import {BLINK_PAY_URL} from "../../../blinkpay/Constants.js";
import {Payment} from "../PaymentStore";
import {UserProfile} from "../UserProfileStore";
import {Merchant} from "../MerchantStore";
import {StoreId} from "../../../stem-core/state/State";

interface LogoutUserRequest {
    userId: number;
    sessionId: number;
}

interface EditUserRequest {
    userId: number;
    [key: string]: any;
}

interface DisableUserRequest {
    userId: number;
    reactivate: boolean;
}

interface MergeUsersRequest {
    userId: number;
    otherUserId: number;
    finalEmail?: string;
}


@globalStore
export class MerchantUser extends BaseStore("MerchantUser", {dependencies: ["MerchantUserSession"]}) {
    static umMap: Map<string, MerchantUser> = new Map(); // Map by userId and merchantId

    @field(Number) merchantId: number;
    @field("Merchant") merchant: Merchant;
    @field(Number) userId: number;
    @field("UserProfile") user: UserProfile;

    @field("MerchantConversion") conversion: any;

    @field(Date) createdAt: Date;
    @field(Boolean) autoPayEnabled: boolean;
    @field(Boolean) useCustomLimit: boolean;
    @field(Number) articlePriceLimit: number;  // TODO: This should be Money, but there is no currency field here!!!

    @field(Object) formResponses: any;
    @field(Boolean) canReceivePromotionalEmails: boolean;

    // Fields only available for merchants
    @field(String) email: string;
    @field(String) name: string;

    @field(String) emailNewsletterEditKey: string;
    @field(Date) consentDate: Date;
    @field(Date) lastAccessDate: Date;
    @field("MerchantUserSession") lastAccessSession: any;

    @field(Number) lifetimeRevenue: number;
    @field("Payment") latestDonationPayment: Payment;
    @field(Number) lifetimeDonatedAmount: number;
    @field("Donation") latestDonation: any;
    @field("Donation") latestOneTimeDonation: any;
    @field("Donation") latestRecurringDonation: any;

    @field(Object) metadata: any;

    static makeFieldLoader<T extends StoreObject>(this: StoreClass<T>, fieldDescriptor: FieldDescriptor): (value: any, obj: any) => T {
        fieldDescriptor.cacheField = false;
        fieldDescriptor.rawField = fieldDescriptor.rawField || ((key: string) => key + "Id");

        return (value: any, obj: any) => {
            //const store = obj.getStore(this.objectType);
            return (this as any as typeof MerchantUser).getByUserAndMerchant(value as number, obj.merchantId) as any;
        }
    }

    getEmail(): string {
        return this.email;
    }

    getName(): string {
        return this.name;
    }

    getLastLocation(): any {
        return this.lastAccessSession?.location;
    }

    getLastDevice(): string | null {
        return this.lastAccessSession?.device;
    }

    getLifetimeRevenue(): Money {
        return new Money(this.lifetimeRevenue, this.merchant.currency.id);
    }

    getLifetimeDonated(): Money {
        return new Money(this.lifetimeDonatedAmount, this.merchant.currency.id);
    }

    getEmailPreferencesLink(): string {
        return `${BLINK_PAY_URL}/email-preferences?edit-key=${this.emailNewsletterEditKey}`;
    }

    static getUMKey(userId: number, merchantId: number): string {
        return `${userId}__${merchantId}`;
    }

    static getByUserAndMerchant(userId: number, merchantId: number): MerchantUser | undefined {
        const key = this.getUMKey(userId, merchantId);
        return this.umMap.get(key);
    }

    static addObject<T extends StoreObject>(this: typeof StoreObject, id: NonNullable<StoreId>, obj: T): void {
        super.addObject<T>(id, obj as any as T);
        const merchantObj = obj as any as MerchantUser;
        const key = (this as typeof MerchantUser).getUMKey(merchantObj.userId, merchantObj.merchantId);
        (this as typeof MerchantUser).umMap.set(key, merchantObj);
    }

    static getFromObject(obj: any): MerchantUser | undefined {
        return this.getByUserAndMerchant(obj.userId, obj.merchantId);
    }
}


@globalStore
export class MerchantUserSession extends BaseStore("MerchantUserSession") {
    @field(Number) merchantId: number;
    @field(Number) userId: number;
    @field(String) key: string; // Optionally, in case we can login as that user
    @field(Date) createdAt: Date;
    @field(Date) expiresAt: Date;
    @field(Date) revokedAt: Date;
    @field(Object) location: any;
    @field(Object) device: any;
}


export async function apiMerchantLogoutUser(userSession: MerchantUserSession): Promise<any> {
    return apiClient.post("/merchant/logout_user", {
        userId: userSession.userId,
        sessionId: userSession.id,
    } as LogoutUserRequest)
}


export async function apiUpdateUserMerchantPreferences(preferences: any = {}): Promise<MerchantUser> {
    const response = await apiClient.post("/preferences/update_user_merchant_preferences", preferences);
    return MerchantUser.loadObject(response);
}

export async function apiMerchantGenerateUserToken(request: any): Promise<MerchantUserSession> {
    const response = await apiClient.post("/merchant/fake_user_session", request);
    return MerchantUserSession.loadObject(response);
}

export async function apiMerchantEditUser(merchantUser: MerchantUser, request: EditUserRequest): Promise<MerchantUser> {
    const response = await apiClient.post("/merchant/edit_user", {
        userId: merchantUser.userId,
        ...request,
    });
    return MerchantUser.loadObject(response);
}

export async function apiMerchantSyncExternalUser(request: any): Promise<any> {
    return apiClient.post("/merchant/sync_external_user", request);
}

export async function apiMerchantDisableUser(merchantUser: MerchantUser, shouldBeActive: boolean): Promise<any> {
    return apiClient.post("/merchant/disable_user", {
        userId: merchantUser.userId,
        reactivate: shouldBeActive,
    } as DisableUserRequest);
}

export async function apiMerchantMergeUsers(merchantUser: MerchantUser, otherMerchantUser: MerchantUser, finalEmail?: string): Promise<any> {
    const response = await apiClient.post("/merchant/merge_users", {
        userId: merchantUser.userId,
        otherUserId: otherMerchantUser.userId,
        finalEmail,
    } as MergeUsersRequest);
    // Let user tables know they should refetch.
    MerchantUser.dispatch("usersMerged", response);
    return response;
}
